import React, { useState, useEffect } from 'react';
import {
  X,
  Mail,
  Send,
  Loader2,
  AlertTriangle,
  Users,
  Pencil,
  Globe,
  Linkedin,
} from 'lucide-react';
import { CampaignCreationState, GmailConnection } from './campaign/types';
import { supabase } from '../../lib/supabase';
import { colors } from '../../styles/tokens';
import { formatVariableName } from '../../utils/nameUtils';
import { formatEmailBodyForPreview } from '../../utils/templateUtils';

interface Recipient {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  company?: string;
  [key: string]: any;
}

interface CampaignLaunchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLaunch: () => void;
  isLoading: boolean;
  campaignState: CampaignCreationState;
  tableName: string;
  tableId: string;
  selectedLeadIds?: string[];
  onUpdateState?: (updates: Partial<CampaignCreationState>) => void;
  onEditSettings?: () => void; // Close modal and go back to settings in chat
  onEditTemplate?: () => void; // Close modal and go back to template selection
  // Direct send mode props
  columns?: { key: string; name: string }[];
  emailColumn?: string;
  onEmailColumnChange?: (column: string) => void;
  gmailConnections?: GmailConnection[];
}

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time', short: 'ET' },
  { value: 'America/Chicago', label: 'Central Time', short: 'CT' },
  { value: 'America/Denver', label: 'Mountain Time', short: 'MT' },
  { value: 'America/Los_Angeles', label: 'Pacific Time', short: 'PT' },
  { value: 'Europe/London', label: 'London', short: 'GMT' },
  { value: 'Europe/Paris', label: 'Paris', short: 'CET' },
  { value: 'Asia/Singapore', label: 'Singapore', short: 'SGT' },
  { value: 'Asia/Tokyo', label: 'Tokyo', short: 'JST' },
];

const formatHourCompact = (hour: number): string => {
  if (hour === 0) return '12am';
  if (hour < 12) return `${hour}am`;
  if (hour === 12) return '12pm';
  return `${hour - 12}pm`;
};

type TabType = 'preview' | 'recipients';

export const CampaignLaunchModal: React.FC<CampaignLaunchModalProps> = ({
  isOpen,
  onClose,
  onLaunch,
  isLoading,
  campaignState,
  tableId,
  selectedLeadIds,
  onEditSettings,
  onEditTemplate,
  onUpdateState,
  columns,
  emailColumn,
  onEmailColumnChange,
  gmailConnections,
}) => {
  // Direct send mode is when onEditSettings is not provided
  const isDirectSendMode = !onEditSettings;
  const [activeTab, setActiveTab] = useState<TabType>('preview');
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  // Fetch recipients when modal opens or tab changes to recipients
  useEffect(() => {
    if (isOpen && activeTab === 'recipients' && recipients.length === 0) {
      const fetchRecipients = async () => {
        setLoadingRecipients(true);
        try {
          // Build query - filter by selectedLeadIds if provided
          let query = supabase
            .from('lead_rows')
            .select(`
              id,
              lead_cells (
                value,
                lead_columns (
                  name
                )
              )
            `)
            .eq('lead_table_id', tableId);

          // Filter by selected leads if partial selection
          if (selectedLeadIds && selectedLeadIds.length > 0) {
            query = query.in('id', selectedLeadIds);
          }

          const { data: rows, error } = await query.limit(50);

          if (error) {
            console.error('Supabase error:', error);
            return;
          }

          if (rows && rows.length > 0) {
            // Transform rows: convert cells array to flat object with column names as keys
            const transformedRows = rows.map((row: any) => {
              const rowData: Recipient = { id: row.id };
              if (row.lead_cells && Array.isArray(row.lead_cells)) {
                row.lead_cells.forEach((cell: any) => {
                  const columnName = cell.lead_columns?.name;
                  if (columnName) {
                    rowData[columnName] = cell.value;
                  }
                });
              }
              return rowData;
            });
            setRecipients(transformedRows);
          }
        } catch (err) {
          console.error('Failed to fetch recipients:', err);
        } finally {
          setLoadingRecipients(false);
        }
      };
      fetchRecipients();
    }
  }, [isOpen, activeTab, tableId, recipients.length, selectedLeadIds]);

  if (!isOpen) return null;

  const timezone = TIMEZONE_OPTIONS.find(tz => tz.value === campaignState.timezone);
  const activeDays = campaignState.daysOfWeek.length;
  const estimatedDays = Math.ceil(
    campaignState.leadCount / (campaignState.dailyLimit * (activeDays || 1) / 7)
  );

  const hasValidationIssues = campaignState.hasValidationIssues &&
    campaignState.variableStatus.some(v => v.status !== 'ok');

  // Extract variables from template
  const extractVariables = (text: string): string[] => {
    const regex = /\{\{([^}]+)\}\}/g;
    const variables: string[] = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      const variable = match[1].trim();
      if (!variables.includes(variable)) {
        variables.push(variable);
      }
    }
    return variables;
  };

  // Get template variables for table columns
  const templateVariables = campaignState.template
    ? extractVariables(`${campaignState.template.subject} ${campaignState.template.body}`)
    : [];

  // Get value from recipient for a variable name
  const getRecipientValue = (recipient: Recipient, variable: string): string => {
    // Try exact match first
    if (recipient[variable] !== undefined) return String(recipient[variable] || '—');

    // Try lowercase
    const lowerVar = variable.toLowerCase();
    if (recipient[lowerVar] !== undefined) return String(recipient[lowerVar] || '—');

    // Try common variations
    const variations: Record<string, string[]> = {
      'first_name': ['firstName', 'First Name', 'first'],
      'last_name': ['lastName', 'Last Name', 'last'],
      'email': ['Email', 'Email Address', 'work_email'],
      'company': ['Company', 'company_name', 'Company Name', 'organization'],
      'title': ['Title', 'job_title', 'Job Title'],
      'phone': ['Phone', 'phone_number', 'Phone Number'],
    };

    const varLower = variable.toLowerCase().replace(/\s+/g, '_');
    const alts = variations[varLower] || [];
    for (const alt of alts) {
      if (recipient[alt] !== undefined) return String(recipient[alt] || '—');
    }

    return '—';
  };


  // Check where a variable is used in the template
  const getVariableUsage = (variable: string): { inSubject: boolean; inBody: boolean } => {
    const subject = campaignState.template?.subject || '';
    const body = campaignState.template?.body || '';
    const regex = new RegExp(`\\{\\{\\s*${variable}\\s*\\}\\}`, 'i');
    return {
      inSubject: regex.test(subject),
      inBody: regex.test(body)
    };
  };

  // Detect if a value is an email
  const isEmail = (value: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  // Detect if a value is a URL
  const isUrl = (value: string): boolean => {
    return /^(https?:\/\/|www\.)/i.test(value) ||
           /\.(com|org|net|io|co|ai|app|dev)$/i.test(value);
  };

  // Detect if a value is a LinkedIn URL
  const isLinkedInUrl = (value: string): boolean => {
    return /linkedin\.com/i.test(value);
  };

  // Format URL for display
  const formatUrl = (url: string): string => {
    return url.replace(/^(https?:\/\/)?(www\.)?/i, '').replace(/\/$/, '');
  };

  // Ensure URL has protocol
  const ensureProtocol = (url: string): string => {
    if (/^https?:\/\//i.test(url)) return url;
    return `https://${url}`;
  };

  // Get days display string
  const getDaysDisplay = () => {
    if (activeDays === 7) return 'Every day';
    if (activeDays === 5 && [1,2,3,4,5].every(d => campaignState.daysOfWeek.includes(d))) {
      return 'Weekdays only';
    }
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return campaignState.daysOfWeek.sort((a, b) => a - b).map(d => dayNames[d]).join(', ');
  };

  // Get recipient display name
  const getRecipientName = (r: Recipient) => {
    const first = r.first_name || r.firstName || r['First Name'] || '';
    const last = r.last_name || r.lastName || r['Last Name'] || '';
    if (first || last) return `${first} ${last}`.trim();
    return r.name || r.Name || '—';
  };

  // Get recipient email
  const getRecipientEmail = (r: Recipient) => {
    return r.email || r.Email || r['Email Address'] || r.work_email || '—';
  };

  // Get recipient company
  const getRecipientCompany = (r: Recipient) => {
    return r.company || r.Company || r.company_name || r['Company Name'] || r.organization || '—';
  };

  return (
    <div className="fixed inset-0 z-[9999]">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl pointer-events-auto flex flex-col"
          style={{ height: 'min(680px, calc(100vh - 48px))' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
            <h2 className="text-[16px] font-semibold text-gray-900">
              {onEditSettings ? 'Review Campaign' : 'Review & Send'}
            </h2>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-100 px-6 flex-shrink-0">
            {[
              { id: 'preview' as TabType, label: 'Email', icon: Mail },
              { id: 'recipients' as TabType, label: `Recipients`, icon: Users, count: campaignState.leadCount },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3.5 text-[13px] border-b-2 -mb-px transition-colors ${
                  activeTab === tab.id
                    ? 'text-gray-900 border-gray-900 font-medium'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count.toLocaleString()}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Validation Warning - shown on all tabs if there are issues */}
          {hasValidationIssues && (
            <div className="mx-6 mt-4 mb-4 px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-lg flex items-center gap-2.5">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <p className="text-[12px] text-amber-700">
                Some template variables don't match your data columns. Check the Recipients tab to verify.
              </p>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-h-0">
            {/* Email Preview Tab */}
            {activeTab === 'preview' && (
              <div className="h-full flex">
                {/* Email */}
                <div className={`flex-1 overflow-y-auto ${hasValidationIssues ? 'px-6 pb-6 pt-2' : 'p-6'}`}>

                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                    {/* Email Header */}
                    <div className="bg-gray-50 border-b border-gray-200 p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center flex-shrink-0">
                          <span className="text-[14px] font-medium text-gray-600">
                            {(campaignState.emailAccount?.gmail_email || 'U')[0].toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-[14px] font-medium text-gray-900">
                              {campaignState.emailAccount?.gmail_email?.split('@')[0] || 'You'}
                            </span>
                            <span className="text-[12px] text-gray-400">
                              &lt;{campaignState.emailAccount?.gmail_email || 'you@example.com'}&gt;
                            </span>
                          </div>
                          <div className="text-[12px] text-gray-500 mt-0.5">
                            to <button
                              onClick={() => setActiveTab('recipients')}
                              className="inline-flex items-center px-1.5 py-0.5 bg-sky-50 text-sky-700 text-[11px] font-medium rounded hover:bg-sky-100 transition-colors"
                            >
                              {'{{recipient_email}}'}
                            </button>
                          </div>
                        </div>
                        <button
                          onClick={onEditTemplate}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors flex-shrink-0"
                        >
                          <Pencil className="w-3 h-3" />
                          Change template
                        </button>
                      </div>
                      <div className="mt-4">
                        <h3 className="text-[17px] font-semibold text-gray-900 leading-snug">
                          {campaignState.template?.subject || 'No subject'}
                        </h3>
                      </div>
                    </div>

                    {/* Email Body */}
                    <div className="p-5">
                      <style>{`
                        .email-body p { margin-bottom: 0.875em; line-height: 1.6; }
                        .email-body p:last-child { margin-bottom: 0; }
                        .email-body br { display: block; content: ""; margin: 0.4em 0; }
                        .email-body ul, .email-body ol { margin: 0.875em 0; padding-left: 1.5em; }
                        .email-body li { margin-bottom: 0.4em; }
                        .email-body a { color: #2563eb; text-decoration: underline; }
                        .email-body .variable-pill {
                          display: inline-flex;
                          align-items: center;
                          padding: 1px 6px;
                          background-color: #f0f9ff;
                          color: #0369a1;
                          font-size: 12px;
                          font-weight: 500;
                          border-radius: 4px;
                          cursor: pointer;
                          transition: background-color 0.15s;
                        }
                        .email-body .variable-pill:hover {
                          background-color: #e0f2fe;
                        }
                      `}</style>
                      <div
                        className="email-body text-[13px] text-gray-700"
                        onClick={(e) => {
                          const target = e.target as HTMLElement;
                          if (target.classList.contains('variable-pill')) {
                            setActiveTab('recipients');
                          }
                        }}
                        dangerouslySetInnerHTML={{
                          __html: formatEmailBodyForPreview(campaignState.template?.body || '<p>No content</p>')
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className={`w-64 bg-gray-50 border-l border-gray-100 flex flex-col gap-4 overflow-y-auto ${hasValidationIssues ? 'px-5 pb-5 pt-3' : 'p-5'}`}>
                  {/* From - editable in direct send mode */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">From</div>
                      {onEditSettings && (
                        <button
                          onClick={onEditSettings}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                          title="Edit sender"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {isDirectSendMode && gmailConnections && gmailConnections.length > 0 ? (
                      <select
                        value={campaignState.emailAccountId || ''}
                        onChange={(e) => {
                          const conn = gmailConnections.find(c => c.id === e.target.value);
                          onUpdateState?.({
                            emailAccountId: conn?.id,
                            emailAccount: conn,
                          });
                        }}
                        className="w-full px-2 py-1.5 text-[12px] bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-gray-300"
                      >
                        {gmailConnections.filter(c => c.token_status === 'active').map((conn) => (
                          <option key={conn.id} value={conn.id}>
                            {conn.gmail_email}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-[13px] text-gray-900 truncate">
                        {campaignState.emailAccount?.gmail_email || 'Not selected'}
                      </div>
                    )}
                  </div>

                  <div className="h-px bg-gray-200" />

                  {/* Send To Column - only in direct send mode */}
                  {isDirectSendMode && columns && columns.length > 0 && (
                    <>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Send To</div>
                        </div>
                        <select
                          value={emailColumn || ''}
                          onChange={(e) => onEmailColumnChange?.(e.target.value)}
                          className="w-full px-2 py-1.5 text-[12px] bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-gray-300"
                        >
                          <option value="">Select email column</option>
                          {columns
                            .filter(col =>
                              col.key.toLowerCase().includes('email') ||
                              col.name.toLowerCase().includes('email')
                            )
                            .map((col) => (
                              <option key={col.key} value={col.key}>
                                {col.name}
                              </option>
                            ))}
                          {/* If no email columns, show all columns */}
                          {columns.filter(col =>
                            col.key.toLowerCase().includes('email') ||
                            col.name.toLowerCase().includes('email')
                          ).length === 0 && columns.map((col) => (
                            <option key={col.key} value={col.key}>
                              {col.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="h-px bg-gray-200" />
                    </>
                  )}

                  {/* Recipients */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Recipients</div>
                    </div>
                    <button
                      onClick={() => setActiveTab('recipients')}
                      className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-[12px] font-medium rounded hover:bg-gray-200 transition-colors"
                    >
                      {campaignState.leadCount.toLocaleString()} leads
                    </button>
                  </div>

                  {/* Schedule - only show if onEditSettings is defined (campaign mode) */}
                  {onEditSettings && (
                    <>
                      <div className="h-px bg-gray-200" />

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Schedule</div>
                          <button
                            onClick={onEditSettings}
                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                            title="Edit schedule"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="text-[13px] text-gray-900">
                          {formatHourCompact(campaignState.startHour)}–{formatHourCompact(campaignState.endHour)} {timezone?.short}
                        </div>
                        <div className="text-[12px] text-gray-500 mt-1">{getDaysDisplay()}</div>
                      </div>

                      <div className="h-px bg-gray-200" />

                      {/* Sending Rate */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Sending Rate</div>
                          <button
                            onClick={onEditSettings}
                            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                            title="Edit sending rate"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="text-[13px] text-gray-900">{campaignState.dailyLimit} per day</div>
                        <div className="text-[12px] text-gray-500 mt-1">
                          ~{estimatedDays} {estimatedDays === 1 ? 'day' : 'days'} to complete
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Recipients Tab */}
            {activeTab === 'recipients' && (
              <div className={`h-full flex flex-col ${hasValidationIssues ? '' : 'pt-0'}`}>
                {loadingRecipients ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color: colors.neutral[400] }} />
                  </div>
                ) : recipients.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center px-6">
                    <Users className="w-8 h-8 mb-2" style={{ color: colors.neutral[300] }} />
                    <p className="text-[13px] font-medium" style={{ color: colors.neutral[600] }}>No recipients loaded</p>
                    <p className="text-[11px] mt-0.5" style={{ color: colors.neutral[400] }}>
                      {campaignState.leadCount.toLocaleString()} leads will receive this campaign
                    </p>
                  </div>
                ) : (
                  <div className="flex-1 overflow-auto mx-6 mb-4 border border-gray-100 rounded-lg overflow-hidden">
                    {/* Header row - matching ProfessionalTable */}
                    <div
                      className="flex items-stretch sticky top-0 z-10"
                      style={{
                        backgroundColor: colors.neutral[50],
                        borderBottom: `1px solid ${colors.neutral[100]}`,
                        minHeight: '48px'
                      }}
                    >
                      {/* Row number header */}
                      <div
                        className="flex items-center justify-center text-[11px] font-medium shrink-0"
                        style={{
                          width: '40px',
                          minWidth: '40px',
                          color: colors.neutral[400],
                          borderRight: `1px solid ${colors.neutral[100]}`
                        }}
                      >
                        #
                      </div>
                      {/* Column headers */}
                      {(templateVariables.length > 0 ? templateVariables : ['Name', 'Email', 'Company']).map((col, idx) => {
                        const usage = templateVariables.length > 0 ? getVariableUsage(col) : null;
                        return (
                          <div
                            key={idx}
                            className="flex flex-col justify-center text-[11px] font-medium px-4 py-2"
                            style={{
                              color: colors.neutral[500],
                              borderRight: `1px solid ${colors.neutral[100]}`,
                              minWidth: '140px',
                              flex: '1 1 0%'
                            }}
                          >
                            <span className="truncate">{templateVariables.length > 0 ? formatVariableName(col) : col}</span>
                            {usage && (usage.inSubject || usage.inBody) && (
                              <div className="flex items-center gap-1 mt-0.5">
                                {usage.inSubject && (
                                  <button
                                    onClick={() => setActiveTab('preview')}
                                    className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-medium rounded bg-sky-50 text-sky-600 hover:bg-sky-100 transition-colors"
                                  >
                                    Subject
                                  </button>
                                )}
                                {usage.inBody && (
                                  <button
                                    onClick={() => setActiveTab('preview')}
                                    className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-medium rounded bg-sky-50 text-sky-600 hover:bg-sky-100 transition-colors"
                                  >
                                    Body
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Data rows - matching ProfessionalTable */}
                    {recipients.map((recipient, idx) => (
                      <div
                        key={recipient.id || idx}
                        className="flex items-center"
                        style={{
                          backgroundColor: 'white',
                          borderBottom: `1px solid ${colors.neutral[100]}`,
                          minHeight: '40px',
                          transition: 'background-color 80ms ease-out'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.neutral[50]}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        {/* Row number */}
                        <div
                          className="flex items-center justify-center text-[11px] font-medium shrink-0"
                          style={{
                            width: '40px',
                            minWidth: '40px',
                            color: colors.neutral[400],
                            borderRight: `1px solid ${colors.neutral[100]}`
                          }}
                        >
                          {idx + 1}
                        </div>
                        {/* Data cells */}
                        {templateVariables.length > 0 ? (
                          templateVariables.map((variable, vIdx) => {
                            const value = getRecipientValue(recipient, variable);
                            const isEmpty = value === '—';
                            const isEmailValue = !isEmpty && isEmail(value);
                            const isLinkedIn = !isEmpty && isLinkedInUrl(value);
                            const isUrlValue = !isEmpty && !isLinkedIn && isUrl(value);

                            return (
                              <div
                                key={vIdx}
                                className="flex items-center gap-2 text-[13px] px-4 py-2"
                                style={{
                                  color: isEmpty ? colors.neutral[300] : colors.neutral[800],
                                  borderRight: `1px solid ${colors.neutral[100]}`,
                                  minWidth: '140px',
                                  flex: '1 1 0%'
                                }}
                              >
                                <span className="truncate flex-1">{isUrlValue || isLinkedIn ? formatUrl(value) : value}</span>
                                {isEmailValue && (
                                  <a
                                    href={`mailto:${value}`}
                                    className="flex-shrink-0 p-1 rounded hover:bg-gray-100 transition-colors"
                                    title="Send email"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Mail className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                                  </a>
                                )}
                                {isLinkedIn && (
                                  <a
                                    href={ensureProtocol(value)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-shrink-0 p-1 rounded hover:bg-blue-50 transition-colors"
                                    title="Open LinkedIn"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Linkedin className="w-3.5 h-3.5 text-[#0A66C2] hover:text-[#004182]" />
                                  </a>
                                )}
                                {isUrlValue && (
                                  <a
                                    href={ensureProtocol(value)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-shrink-0 p-1 rounded hover:bg-gray-100 transition-colors"
                                    title="Open website"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Globe className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                                  </a>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <>
                            <div
                              className="flex items-center text-[13px] px-4 py-2 truncate"
                              style={{
                                color: colors.neutral[800],
                                borderRight: `1px solid ${colors.neutral[100]}`,
                                minWidth: '140px',
                                flex: '1 1 0%'
                              }}
                            >
                              {getRecipientName(recipient)}
                            </div>
                            <div
                              className="flex items-center gap-2 text-[13px] px-4 py-2"
                              style={{
                                color: colors.neutral[600],
                                borderRight: `1px solid ${colors.neutral[100]}`,
                                minWidth: '140px',
                                flex: '1 1 0%'
                              }}
                            >
                              <span className="truncate flex-1">{getRecipientEmail(recipient)}</span>
                              {getRecipientEmail(recipient) !== '—' && (
                                <a
                                  href={`mailto:${getRecipientEmail(recipient)}`}
                                  className="flex-shrink-0 p-1 rounded hover:bg-gray-100 transition-colors"
                                  title="Send email"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Mail className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                                </a>
                              )}
                            </div>
                            <div
                              className="flex items-center text-[13px] px-4 py-2 truncate"
                              style={{
                                color: colors.neutral[500],
                                borderRight: `1px solid ${colors.neutral[100]}`,
                                minWidth: '140px',
                                flex: '1 1 0%'
                              }}
                            >
                              {getRecipientCompany(recipient)}
                            </div>
                          </>
                        )}
                      </div>
                    ))}

                    {/* Footer showing count */}
                    {campaignState.leadCount > recipients.length && (
                      <div
                        className="flex items-center justify-center py-3"
                        style={{ backgroundColor: colors.neutral[50], borderTop: `1px solid ${colors.neutral[100]}` }}
                      >
                        <span className="text-[11px]" style={{ color: colors.neutral[500] }}>
                          Showing {recipients.length} of {campaignState.leadCount.toLocaleString()} recipients
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2.5 text-[13px] text-gray-600 hover:text-gray-900 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onLaunch}
              disabled={isLoading || (onEditSettings && campaignState.daysOfWeek.length === 0) || !campaignState.emailAccountId || (isDirectSendMode && !emailColumn)}
              className="flex items-center gap-2 px-6 py-2.5 text-[14px] font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {onEditSettings ? 'Launching...' : 'Sending...'}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  {onEditSettings ? 'Launch Campaign' : `Send ${campaignState.leadCount.toLocaleString()} Emails`}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
